import { useSelector, useDispatch } from "react-redux";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useScheduleQuery } from "../../../hooks/api/useSchedule";
import PageTemplate from "../../templates/PageTemplate";
import Error from "../../organisms/Error";
import ApplicationForm from "./ApplicationForm";
import ActivityForm from "../activity/ActivityForm";
import { useEffect } from "react";
import Loading from "../../organisms/Loading";
import ActivityTransfer from "../activity/ActivityTransfer";
import { showAlert } from "../../../reducer/alertSlice";
import { LOCKED_EVENT_IDX } from "../../../config/eventConfig";

const Application = () => {
	const { idx, pageType } = useParams();
    const idxParams = new URLSearchParams("?" + window.atob(idx));
	const eventIdx = idxParams.get("eventIdx");
	const scheduleIdx = idxParams.get("scheduleIdx");
	const isApplicationLocked = Number(eventIdx) === LOCKED_EVENT_IDX;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
	const customersInfo = useSelector(state => state.customers).value;
    const primaryCustomer = customersInfo.filter(v => v.isPrimary)[0]; // 주사업자
	const scheduleQuery = useScheduleQuery(scheduleIdx, primaryCustomer?primaryCustomer.uid:"");

    useEffect(() => {
        if (scheduleQuery.data) {
            document.title = `${window.location.hostname == "localhost" ? "(local) " : ""}${scheduleQuery.data?.title} - ${scheduleQuery.data?.order}차수`;
        }
    },[scheduleQuery.data]);

    useEffect(() => {
        scheduleQuery.refetch();
    }, [pageType]);

    // 접근 제한 모달 (양도 화면, 잠금 행사 제외)
    useEffect(() => {
        if (scheduleQuery.data?.restricted && pageType !== "activityTransfer" && !isApplicationLocked) {
            dispatch(showAlert({
                msg: scheduleQuery.data.restrictionMessage,
                btnText: "확인",
                func: () => navigate("/", { replace: true })
            }));
        }
    }, [scheduleQuery.data, pageType]);

    if (scheduleQuery.isLoading) {
        return (
            <PageTemplate title="">
                <Loading layer={true}/>
            </PageTemplate>
        )
    }

    {/* 신청기간 외 화면 */}
    if (scheduleQuery.isError) {
        if (scheduleQuery.error.response) {
            const errorData = scheduleQuery.error.response.data.data;
            const errorMsg = scheduleQuery.error.response.data.msg?.ko?.replace(/\n/g, <br/>) || "오류가 발생했습니다.";

            // 양도 기간 내 && 양도 페이지 접근인 경우 에러 화면 대신 양도 페이지 렌더링
            if (pageType === "activityTransfer" && errorData?.activity_transfer_visible) {
                return (
                    <PageTemplate title={`${errorData?.title || ""} - ${errorData?.order || ""}차수`} showBack={true}>
                        <ActivityTransfer scheduleIdx={scheduleIdx} urlPath={idx} />
                    </PageTemplate>
                );
            }

            const title = errorData?.title ? errorData?.title : "";
            const order = errorData?.order ? errorData?.order : ""
            return (
                <PageTemplate title={(title && order) ? `${title} - ${order} 차수` : ""}>
                    <Error msg={errorMsg}/>
                </PageTemplate>
            )
        } else {
            return (
                <PageTemplate>
                    <Error msg={"오류가 발생했습니다."}/>
                </PageTemplate>
            )
        }
    }

    // 차단된 상태면 빈 로딩화면만 표시 (모달이 위에 떠있음) — 잠금 행사는 예외
    if (scheduleQuery.data?.restricted && pageType !== "activityTransfer" && !isApplicationLocked) {
        return (
            <PageTemplate title="">
                <Loading layer={true}/>
            </PageTemplate>
        );
    }

    const getPage = () => {
        const applicationIdx = scheduleQuery.data.application_idx;

        // 액티비티 양도/양수 — 신청서 제출 여부와 무관하게 접근 가능
        if (pageType === "activityTransfer") {
            // 양도 기능 접근 제어: activity_transfer_visible이 있으면 우선 사용
            const isTransferAccessible = scheduleQuery.data?.activity_transfer_visible !== undefined
                ? scheduleQuery.data.activity_transfer_visible
                : (scheduleQuery.data?.target === "activity" && Number(scheduleQuery.data?.activity_transfer_enabled) === 1);

            if (!isTransferAccessible) {
                return <Error msg={"액티비티 양도 기능이 비활성화 되어있거나\n양도/양수 가능 기간이 아닙니다."} />;
            }
            return (
                <ActivityTransfer
                    scheduleIdx={scheduleIdx}
                    urlPath={idx}
                />
            )
        }

        // 1-1. 신청 정보 있는 경우
        if (applicationIdx) {
            // 2-1. 고객 정보 수정페이지 — 잠금 행사면 완료페이지로 리다이렉트
            if (pageType === "modify") {
                if (isApplicationLocked) {
                    return <Navigate to={"/application/complete/" + applicationIdx + "/" + scheduleIdx} replace />;
                }
                return (
                    <ApplicationForm 
                        target={scheduleQuery.data.target} 
                        eventIdx={eventIdx}
                        scheduleIdx={scheduleIdx}
                        applicationIdx={applicationIdx}
                        urlPath={idx}
                    />
                )
            }

            // 2-2. 액티비티 신청 페이지
            if (pageType === "activity") {
                return (
                    <ActivityForm 
                        scheduleIdx={scheduleIdx} 
                        applicationIdx={applicationIdx}
                        target={scheduleQuery.data.target}
                        urlPath={idx}
                    />
                )
            }

            // 2-3. 신청 완료 페이지
            return <Navigate to={"/application/complete/" + applicationIdx + "/" + scheduleIdx} replace />;
        } 
        // 1-2. 신청 정보 없는 경우
        else {
            return (
                <ApplicationForm 
                    target={scheduleQuery.data.target} 
                    eventIdx={eventIdx}
                    scheduleIdx={scheduleIdx}
                    urlPath={idx}
                />
            )
        }
    }

    if (scheduleQuery.data) {
        return (
            <PageTemplate title={`${scheduleQuery.data?.title} - ${scheduleQuery.data?.order}차수`} showBack={true}>
                {getPage()}
            </PageTemplate>
        )
    }

}
export default Application